import { useTranslation } from '@/app/providers';
import { Skeleton } from '@/shared/components/skeleton';
import { cn } from '@/shared/lib/cn';

const PREFERENCE_ROW_COUNT = 4;

function LabelAndInputSkeleton({
  className,
  withSplitInput = false,
}: {
  className?: string;
  withSplitInput?: boolean;
}) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <Skeleton className="h-4 w-20 rounded-md" />
      {withSplitInput ? (
        <div className="flex h-12 items-center overflow-hidden rounded-xs border border-border bg-card">
          <Skeleton className="h-full w-28 shrink-0 rounded-none rounded-l-xs" />
          <Skeleton className="h-full min-w-0 flex-1 rounded-none rounded-r-xs" />
        </div>
      ) : (
        <Skeleton className="h-12 w-full rounded-xs" />
      )}
    </div>
  );
}

/**
 * Layout-shaped loading state for the public fidelity form (`/fidelityfrm`).
 * Matches the gradient aside + form shell so Suspense and API load feel continuous.
 */
export function PublicFidelityFormSkeleton() {
  const { t } = useTranslation();

  return (
    <main
      className="min-h-screen bg-content-bg lg:h-dvh lg:overflow-hidden"
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={t('publicFidelity.loading')}
    >
      <div className="lg:grid lg:h-full lg:grid-cols-[minmax(280px,40%)_minmax(0,1fr)]">
        <aside
          className="relative flex min-h-56 shrink-0 flex-col justify-end overflow-hidden p-5 sm:min-h-64 sm:p-7 lg:h-full lg:p-8"
          style={{
            background:
              'linear-gradient(165deg, #4F46E5 0%, color-mix(in srgb, #4F46E5 72%, #0f172a) 55%, #0f172a 100%)',
          }}
        >
          <div
            className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-white/10 blur-2xl"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute -bottom-20 left-8 h-40 w-40 rounded-full bg-white/5 blur-2xl"
            aria-hidden
          />

          <div className="relative flex h-full flex-col justify-between gap-5">
            <div className="flex items-start justify-between gap-3">
              <Skeleton
                variant="circular"
                className="h-11 w-11 bg-white/90"
              />
              <Skeleton className="h-6 w-16 rounded-full bg-white/20" />
            </div>

            <div className="space-y-2">
              <Skeleton className="h-3 w-36 rounded-md bg-white/25" />
              <Skeleton className="h-8 w-52 max-w-[90%] rounded-md bg-white/40 sm:h-9 sm:w-60" />
              <Skeleton className="h-3.5 w-64 max-w-full rounded-md bg-white/20" />
              <Skeleton className="h-3.5 w-48 max-w-full rounded-md bg-white/15" />
            </div>

            <div className="relative overflow-hidden rounded-2xl border border-white/20 bg-white/10 p-4 backdrop-blur-sm">
              <Skeleton className="absolute inset-y-0 left-0 w-1.5 rounded-none bg-white/50" />
              <Skeleton className="h-2.5 w-24 rounded-md bg-white/25" />
              <Skeleton className="mt-2 h-5 w-40 rounded-md bg-white/35" />
              <div className="mt-3 flex items-center justify-between gap-2">
                <Skeleton className="h-3.5 w-32 rounded-md bg-white/20" />
                <Skeleton
                  variant="circular"
                  className="h-8 w-8 bg-white/40 ring-2 ring-white/30"
                />
              </div>
            </div>
          </div>
        </aside>

        <section className="flex min-h-0 flex-col bg-card lg:overflow-y-auto">
          <div
            className={cn(
              'mx-auto flex w-full max-w-lg flex-1 flex-col',
              'px-4 py-5 sm:px-7 sm:py-6 lg:px-8 lg:py-7',
            )}
          >
            <div className="mb-3 flex justify-end">
              <Skeleton className="h-9 w-9 rounded-md" />
            </div>

            <div className="flex min-h-0 flex-1 flex-col gap-5">
              <div className="space-y-2">
                <Skeleton className="h-7 w-48 max-w-full rounded-md sm:h-8 sm:w-56" />
                <Skeleton className="h-4 w-72 max-w-full rounded-md" />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <LabelAndInputSkeleton />
                <LabelAndInputSkeleton />
                <LabelAndInputSkeleton
                  className="sm:col-span-2"
                  withSplitInput
                />
                <LabelAndInputSkeleton className="sm:col-span-2" />
              </div>

              <div className="space-y-3 rounded-xl border border-border bg-muted/20 p-4">
                <div className="flex items-center justify-between gap-3">
                  <Skeleton className="h-4 w-32 rounded-md" />
                  <Skeleton className="h-3.5 w-14 rounded-md" />
                </div>
                {Array.from({ length: PREFERENCE_ROW_COUNT }, (_, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Skeleton className="h-4 w-4 shrink-0 rounded-sm" />
                    <Skeleton
                      className={cn(
                        'h-3.5 rounded-md',
                        index === 3 ? 'w-24' : 'w-52 max-w-[85%]',
                      )}
                    />
                  </div>
                ))}
              </div>

              <Skeleton className="mt-auto h-12 w-full rounded-xs" />
              <Skeleton className="mx-auto h-3 w-28 rounded-md" />
            </div>
          </div>
        </section>
      </div>
      <span className="sr-only">{t('publicFidelity.loading')}</span>
    </main>
  );
}
